/**
 * Hero background SPEC — the pure, React-free core of the hero-background resolver.
 *
 * This module is importable on a backend / server runtime (the cascade) with ZERO
 * React in its dependency graph: it holds only the TYPES, the legal-space rules,
 * the named-preset menu, and the layer-descriptor logic. The React renderer that
 * turns a descriptor into JSX lives in ./hero-background.tsx and imports from here.
 *
 * - The TYPES make every illegal background unrepresentable (Beams never combines,
 *   a Spotlight only pairs with a `top` grid, Ambient never mixes with Visual,
 *   a combined Glow has no position — it inherits the grid's).
 * - `heroBackgroundLayers(spec)` resolves a spec to the ordered, named layer
 *   descriptors (`{ name, props }`) — pure data, no components.
 * - `HERO_BACKGROUNDS` is the named-preset menu (legal-by-construction shortcuts).
 * - `allLegalHeroBackgrounds()` enumerates the FULL legal space (every legal
 *   shape, including the Pattern+Ambient and Spotlight+top-grid COMBINATIONS) so a
 *   consumer can draw a varied background per site without re-implementing the rules.
 */

// ─── Types (the full legal space; illegal shapes are unrepresentable) ──────────

export type GridStyle = 'dashed' | 'diagonal' | 'sparse' | 'crosshatch' | 'perspective'

export interface GridPositions {
  dashed: 'top' | 'center' | 'bottom'
  diagonal: 'top' | 'center'
  sparse: 'top' | 'center'
  crosshatch: 'top' | 'center'
  perspective: 'top' | 'bottom'
}

export type PatternBackground = {
  [S in GridStyle]: {
    style: S
    position: GridPositions[S]
    animate?: boolean
    animationDelay?: number
  }
}[GridStyle]

/** A grid pinned to the top — the only patterns a Spotlight may pair with (R2). */
export type TopGridPattern = {
  style: GridStyle
  position: 'top'
  animate?: boolean
  animationDelay?: number
}

export type GlowColor = 'primary' | 'accent' | 'muted'
export type GlowPosition = 'top' | 'center' | 'bottom'

/** Ambient alone — a glow picks its own position. */
export type AmbientBackground =
  | { kind: 'glow'; position?: GlowPosition; color?: GlowColor; animate?: boolean }
  | { kind: 'gradientBlur' }

/** Ambient combined with a pattern — a glow has NO position; it inherits the grid's (R1/R5). */
export type AmbientCombine =
  | { kind: 'glow'; color?: GlowColor; animate?: boolean }
  | { kind: 'gradientBlur' }

export type SpotlightColor = 'primary' | 'accent' | 'muted' | 'foreground'
export type SpotlightPosition = 'top-left' | 'top-right'
export type BeamsPosition = 'center' | 'top'

export interface PatternSection {
  pattern: PatternBackground
  ambient?: AmbientCombine
}
export interface AmbientSection {
  ambient: AmbientBackground
}
export interface SpotlightSection {
  visual: { kind: 'spotlight'; position?: SpotlightPosition; color?: SpotlightColor; animate?: boolean }
  pattern?: TopGridPattern
}
export interface BeamsSection {
  visual: { kind: 'beams'; position?: BeamsPosition }
}
export type SectionBackground = PatternSection | AmbientSection | SpotlightSection | BeamsSection

export interface PageBackground {
  noise?: { fixed?: boolean }
}

// ─── Layer descriptors (pure) ──────────────────────────────────────────────────
//
// The names below are the background components the React renderer maps over (see
// REGISTRY in ./hero-background.tsx). Kept as a standalone literal union so this
// module never has to import the components themselves.

export type ComponentName =
  | 'GridDashedTop'
  | 'GridDashedCenter'
  | 'GridDashedBottom'
  | 'GridDiagonalTop'
  | 'GridDiagonalCenter'
  | 'GridSparseTop'
  | 'GridSparseCenter'
  | 'GridCrosshatchTop'
  | 'GridCrosshatchCenter'
  | 'GridPerspectiveTop'
  | 'GridPerspectiveBottom'
  | 'GlowTop'
  | 'GlowCenter'
  | 'GlowBottom'
  | 'GradientBlur'
  | 'SpotlightTopLeft'
  | 'SpotlightTopRight'
  | 'Beams'
  | 'BeamsTop'

const pascal = (s: string) =>
  s
    .split('-')
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join('')

export type HeroBackgroundLayer = { name: ComponentName; props: Record<string, unknown> }

function patternLayer(p: PatternBackground | TopGridPattern): HeroBackgroundLayer {
  return {
    name: `Grid${pascal(p.style)}${pascal(p.position)}` as ComponentName,
    props: { animate: p.animate, animationDelay: p.animationDelay },
  }
}

/** `inheritPos` is supplied ONLY when the glow is combined with a pattern (R1/R5). */
function ambientLayer(a: AmbientBackground | AmbientCombine, inheritPos?: GlowPosition): HeroBackgroundLayer {
  if (a.kind === 'gradientBlur') return { name: 'GradientBlur', props: {} }
  const pos = inheritPos ?? ('position' in a ? a.position : undefined) ?? 'top'
  return { name: `Glow${pascal(pos)}` as ComponentName, props: { color: a.color, animate: a.animate } }
}

function spotlightLayer(v: SpotlightSection['visual']): HeroBackgroundLayer {
  return {
    name: `Spotlight${pascal(v.position ?? 'top-left')}` as ComponentName,
    props: { color: v.color, animate: v.animate },
  }
}

function beamsLayer(v: BeamsSection['visual']): HeroBackgroundLayer {
  return { name: v.position === 'top' ? 'BeamsTop' : 'Beams', props: {} }
}

/**
 * The ordered layer descriptors for a spec — exported so the rules are unit-testable
 * without scraping the DOM. Render order (R5): the grid Pattern is the base layer
 * (painted first / behind); the accent (Ambient wash or Spotlight) is painted last
 * (in front). Beams always renders alone (R3).
 */
export function heroBackgroundLayers(bg: SectionBackground): HeroBackgroundLayer[] {
  if ('visual' in bg) {
    const visual = bg.visual
    if (visual.kind === 'beams') return [beamsLayer(visual)] // R3 — alone
    const out: HeroBackgroundLayer[] = []
    if ('pattern' in bg && bg.pattern) out.push(patternLayer(bg.pattern)) // R2 — top grid behind
    out.push(spotlightLayer(visual)) //                                     spotlight shines on top
    return out
  }
  if ('pattern' in bg) {
    const out: HeroBackgroundLayer[] = [patternLayer(bg.pattern)] // grid behind
    if (bg.ambient) out.push(ambientLayer(bg.ambient, bg.pattern.position)) // R1 — ambient blooms on top
    return out
  }
  return [ambientLayer(bg.ambient)]
}

// ─── Named preset menu (for MANUAL / explicit use only) ────────────────────────
//
// Convenience shortcuts for a human composing a one-off page by name, e.g.
// `heroBackground(HERO_BACKGROUNDS['beams-center'])`. These are NOT the generation
// pool and impose NO limit on it — seeded per-site draws sample the FULL legal
// space via `allLegalHeroBackgrounds()` (every legal shape, including combinations).

export const HERO_BACKGROUNDS = {
  // Pattern only
  'dashed-top': { pattern: { style: 'dashed', position: 'top' } },
  'perspective-bottom': { pattern: { style: 'perspective', position: 'bottom' } },
  'crosshatch-top': { pattern: { style: 'crosshatch', position: 'top' } },
  // Pattern + Ambient (R1)
  'dashed-top + blur': { pattern: { style: 'dashed', position: 'top' }, ambient: { kind: 'gradientBlur' } },
  'dashed-top + glow': { pattern: { style: 'dashed', position: 'top' }, ambient: { kind: 'glow' } },
  'sparse-top + blur': { pattern: { style: 'sparse', position: 'top' }, ambient: { kind: 'gradientBlur' } },
  // Spotlight + top-grid (R2)
  'spotlight-top-left + dashed-top': {
    visual: { kind: 'spotlight', position: 'top-left' },
    pattern: { style: 'dashed', position: 'top' },
  },
  'spotlight-top-right + crosshatch-top': {
    visual: { kind: 'spotlight', position: 'top-right' },
    pattern: { style: 'crosshatch', position: 'top' },
  },
  // Ambient only
  'gradient-blur': { ambient: { kind: 'gradientBlur' } },
  'glow-top': { ambient: { kind: 'glow', position: 'top' } },
  // Visual only
  'spotlight-top-left': { visual: { kind: 'spotlight', position: 'top-left' } },
  'beams-center': { visual: { kind: 'beams' } },
  'beams-top': { visual: { kind: 'beams', position: 'top' } },
} satisfies Record<string, SectionBackground>

export type HeroBackgroundPreset = keyof typeof HERO_BACKGROUNDS

// ─── Full-space enumerator (for seeded per-site draws) ─────────────────────────

const GRID_STYLES: GridStyle[] = ['dashed', 'diagonal', 'sparse', 'crosshatch', 'perspective']
const GRID_POSITIONS: { [S in GridStyle]: GridPositions[S][] } = {
  dashed: ['top', 'center', 'bottom'],
  diagonal: ['top', 'center'],
  sparse: ['top', 'center'],
  crosshatch: ['top', 'center'],
  perspective: ['top', 'bottom'],
}
const GLOW_COLORS: GlowColor[] = ['primary', 'accent', 'muted']
const GLOW_POSITIONS: GlowPosition[] = ['top', 'center', 'bottom']
const SPOTLIGHT_COLORS: SpotlightColor[] = ['primary', 'accent', 'muted', 'foreground']
const SPOTLIGHT_POSITIONS: SpotlightPosition[] = ['top-left', 'top-right']
const BEAMS_POSITIONS: BeamsPosition[] = ['center', 'top']
const TOP_GRID_STYLES: GridStyle[] = ['dashed', 'diagonal', 'sparse', 'crosshatch', 'perspective']

function allPatterns(): PatternBackground[] {
  const out: PatternBackground[] = []
  for (const style of GRID_STYLES) {
    for (const position of GRID_POSITIONS[style]) {
      out.push({ style, position } as PatternBackground)
    }
  }
  return out
}

/**
 * Every legal `SectionBackground`, generated from the rules (not hand-listed) in a
 * STABLE order — so a seeded draw maps the same seed to the same background. Covers
 * all six legal shapes, INCLUDING the Pattern+Ambient and Spotlight+top-grid
 * combinations (so a draw yields layered backgrounds, not just single components).
 */
export function allLegalHeroBackgrounds(): SectionBackground[] {
  const out: SectionBackground[] = []
  const patterns = allPatterns()

  // 1. Pattern alone
  for (const pattern of patterns) out.push({ pattern })

  // 2. Pattern + Ambient (R1) — gradientBlur or a color-varied glow that inherits position
  for (const pattern of patterns) {
    out.push({ pattern, ambient: { kind: 'gradientBlur' } })
    for (const color of GLOW_COLORS) out.push({ pattern, ambient: { kind: 'glow', color } })
  }

  // 3. Ambient alone
  out.push({ ambient: { kind: 'gradientBlur' } })
  for (const position of GLOW_POSITIONS) {
    for (const color of GLOW_COLORS) out.push({ ambient: { kind: 'glow', position, color } })
  }

  // 4. Spotlight alone
  for (const position of SPOTLIGHT_POSITIONS) {
    for (const color of SPOTLIGHT_COLORS) out.push({ visual: { kind: 'spotlight', position, color } })
  }

  // 5. Spotlight + top-grid (R2)
  for (const position of SPOTLIGHT_POSITIONS) {
    for (const color of SPOTLIGHT_COLORS) {
      for (const style of TOP_GRID_STYLES) {
        out.push({ visual: { kind: 'spotlight', position, color }, pattern: { style, position: 'top' } })
      }
    }
  }

  // 6. Beams alone (R3)
  for (const position of BEAMS_POSITIONS) out.push({ visual: { kind: 'beams', position } })

  return out
}
